import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

export const useSubscription = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: subscriptionInfo, isLoading: loading, error, refetch: refreshSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await api.get('/payments/subscription');
      return response.data.data;
    },
    enabled: !!user && !!user._id,
    staleTime: 30 * 1000, // 30 seconds - más frecuente para sincronización
    refetchInterval: (data) => {
      // Polling más agresivo si la suscripción está en proceso
      if (data?.status === 'incomplete' || data?.status === 'processing') {
        return 2000; // 2 segundos
      }
      return 10000; // 10 segundos para suscripciones activas
    },
    refetchIntervalInBackground: true,
  });

  // Función para actualización optimista
  const updateSubscriptionOptimistically = (updates) => {
    queryClient.setQueryData(['subscription'], (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        ...updates
      };
    });
  };

  // Función para forzar actualización inmediata
  const forceRefresh = async () => {
    await queryClient.invalidateQueries(['subscription']);
    return refreshSubscription();
  };

  const canUseSubdomains = subscriptionInfo?.permissions?.canUseSubdomains || false;
  const canUseCustomDomains = subscriptionInfo?.permissions?.canUseCustomDomains || false;
  const canUsePremiumTemplates = subscriptionInfo?.permissions?.canUsePremiumTemplates || false;
  const canUseAdvancedAnalytics = subscriptionInfo?.permissions?.canUseAdvancedAnalytics || false;
  const canUseAPI = subscriptionInfo?.permissions?.canUseAPI || false;
  const isPremium = subscriptionInfo?.isPremium || false;
  const isActive = subscriptionInfo?.isActive || false;
  const plan = subscriptionInfo?.plan || 'free';

  return {
    subscriptionInfo,
    loading,
    error,
    refreshSubscription: forceRefresh,
    updateSubscriptionOptimistically,
    // Helpers para verificar permisos
    canUseSubdomains,
    canUseCustomDomains,
    canUsePremiumTemplates,
    canUseAdvancedAnalytics,
    canUseAPI,
    isPremium,
    isActive,
    plan
  };
}; 